import React, {useEffect, useState, ReactElement, useRef, cloneElement, CSSProperties} from 'react';

import {useMediaQuery} from 'src/hooks/useMediaQuery';
import styles from './two-column-masonry.module.scss';

const GAP = 12;
const ROW_HEIGHT = 6;

interface Props {
  children: ReactElement<{style?: CSSProperties; ref?: React.Ref<HTMLDivElement>}>[];
}

const TwoColumnMasonry = ({children}: Props) => {
  const childrenRef = useRef<(HTMLDivElement | null)[]>([]);
  const [childrenHeights, setChildrenHeights] = useState<Record<string, number>>({});
  const mq = useMediaQuery();

  useEffect(() => {
    if (mq.MS_AND_DOWN) return;

    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const newHeights: Record<string, number> = {};

      entries.forEach((entry) => {
        newHeights[entry.target.id] = entry.borderBoxSize[0].blockSize;
      });

      setChildrenHeights((existingHeights) => ({...existingHeights, ...newHeights}));
    });

    childrenRef.current.forEach((child) => {
      if (child) {
        resizeObserver.observe(child.children[0]);
      }
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [mq.MS_AND_DOWN, children]);

  return (
    <div
      className={styles.grid}
      style={{'--gap': `${GAP}px`, '--row-height': `${ROW_HEIGHT}px`} as CSSProperties}
    >
      {children.map((child, index) => {
        const key = child.key;

        return cloneElement(child, {
          ref: (node) => {
            childrenRef.current[index] = node;
          },
          style: {
            gridRowEnd: `span ${Math.ceil(
              (((key ? childrenHeights[key] : 0) || 0) + GAP) / (GAP + ROW_HEIGHT)
            )}`,
          },
        });
      })}
    </div>
  );
};

export default TwoColumnMasonry;
